import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import axios from 'axios';
import { 
  Search, FileText, Globe, Sparkles, TrendingUp, Target, 
  AlertCircle, CheckCircle2, XCircle, Plus, Trash2, RefreshCw, 
  Eye, Copy, Download, Loader2, Calendar, Clock, Send, X, 
  BarChart3, ExternalLink, Zap, Lightbulb, ArrowRight, Award,
  Rocket, Settings, Link2, BookOpen, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  if (score >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

const getGradeColor = (grade) => {
  if (grade === 'A+' || grade === 'A') return 'bg-green-500';
  if (grade === 'B') return 'bg-blue-500';
  if (grade === 'C') return 'bg-yellow-500';
  if (grade === 'D') return 'bg-orange-500';
  return 'bg-red-500';
};

const getDifficultyColor = (level) => {
  if (level === 'Facile') return 'bg-green-100 text-green-700';
  if (level === 'Moyen') return 'bg-yellow-100 text-yellow-700';
  if (level === 'Difficile') return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};

export default function SEOPanel() {
  const [activeSection, setActiveSection] = useState('site-pages');
  const [loading, setLoading] = useState(false);
  
  // Site pages
  const [sitePages, setSitePages] = useState([]);
  const [baseUrl, setBaseUrl] = useState('');
  
  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [cities, setCities] = useState([]);
  
  // Keyword difficulty
  const [keywordToAnalyze, setKeywordToAnalyze] = useState('');
  const [keywordResult, setKeywordResult] = useState(null);
  const [keywordHistory, setKeywordHistory] = useState([]);
  
  // Analyzer
  const [urlToAnalyze, setUrlToAnalyze] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  
  // Generator
  const [generatorType, setGeneratorType] = useState('landing');
  const [selectedCity, setSelectedCity] = useState('');
  const [guideTopic, setGuideTopic] = useState('');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedPages, setGeneratedPages] = useState([]);
  
  // Scheduler
  const [schedulingPageId, setSchedulingPageId] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [seoStats, setSeoStats] = useState(null);
  
  // Meta optimizer
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [optimizedMeta, setOptimizedMeta] = useState(null);
  
  // Robot Speed
  const [robotSpeedStatus, setRobotSpeedStatus] = useState(null);
  const [robotSpeedApiKey, setRobotSpeedApiKey] = useState('');
  const [robotSpeedArticles, setRobotSpeedArticles] = useState([]);
  const [robotSpeedLoading, setRobotSpeedLoading] = useState(false);
  
  // Google Tools
  const [googleStatus, setGoogleStatus] = useState(null);
  const [gaMeasurementId, setGaMeasurementId] = useState('');
  const [gscVerificationCode, setGscVerificationCode] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    loadSitePages();
    loadKeywords();
    loadRecentAnalyses();
    loadGeneratedPages();
    loadSeoStats();
    loadKeywordHistory();
    loadRobotSpeedStatus();
    loadGoogleStatus();
  }, []);

  const loadSitePages = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/site-pages`, { withCredentials: true });
      setSitePages(response.data.pages || []);
      setBaseUrl(response.data.base_url || '');
    } catch (error) {
      console.error('Error loading site pages:', error);
    }
  };

  const loadKeywords = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/keywords`, { withCredentials: true });
      setKeywords(response.data.keywords || []);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error loading keywords:', error);
    }
  };

  const loadRecentAnalyses = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/analyses?limit=10`, { withCredentials: true });
      setRecentAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error('Error loading analyses:', error);
    }
  };

  const loadGeneratedPages = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/pages?limit=20`, { withCredentials: true });
      setGeneratedPages(response.data.pages || []);
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  const loadSeoStats = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/stats`, { withCredentials: true });
      setSeoStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadKeywordHistory = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/keyword-history?limit=10`, { withCredentials: true });
      setKeywordHistory(response.data.analyses || []);
    } catch (error) {
      console.error('Error loading keyword history:', error);
    }
  };

  // Robot Speed functions
  const loadRobotSpeedStatus = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/robot-speed/status`, { withCredentials: true });
      setRobotSpeedStatus(response.data);
      if (response.data.configured) {
        loadRobotSpeedArticles();
      }
    } catch (error) {
      console.error('Error loading Robot Speed status:', error);
    }
  };

  const loadRobotSpeedArticles = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/robot-speed/articles`, { withCredentials: true });
      setRobotSpeedArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error loading Robot Speed articles:', error);
    }
  };

  const configureRobotSpeed = async () => {
    if (!robotSpeedApiKey.startsWith('rs_')) {
      toast.error("La clé API doit commencer par 'rs_'");
      return;
    }
    
    setRobotSpeedLoading(true);
    try {
      await axiosInstance.post(`${API}/seo/robot-speed/configure`, 
        { api_key: robotSpeedApiKey },
        { withCredentials: true }
      );
      toast.success('Robot Speed configuré avec succès');
      setRobotSpeedApiKey('');
      loadRobotSpeedStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de configuration');
    } finally {
      setRobotSpeedLoading(false);
    }
  };

  const syncRobotSpeed = async () => {
    setRobotSpeedLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/seo/robot-speed/sync`, {}, { withCredentials: true });
      toast.success(response.data.message);
      loadRobotSpeedArticles();
      loadRobotSpeedStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setRobotSpeedLoading(false);
    }
  };

  const publishRobotSpeedArticle = async (articleId) => {
    setRobotSpeedLoading(true);
    try {
      const response = await axiosInstance.post(
        `${API}/seo/robot-speed/articles/${articleId}/publish`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      loadRobotSpeedArticles();
      loadGeneratedPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de publication');
    } finally {
      setRobotSpeedLoading(false);
    }
  };

  // Google Tools functions
  const loadGoogleStatus = async () => {
    try {
      const response = await axiosInstance.get(`${API}/seo/google/status`, { withCredentials: true });
      setGoogleStatus(response.data);
    } catch (error) {
      console.error('Error loading Google status:', error);
    }
  };

  const configureGoogleAnalytics = async () => {
    if (!gaMeasurementId.startsWith('G-')) {
      toast.error("L'ID de mesure doit commencer par 'G-'");
      return;
    }
    
    setGoogleLoading(true);
    try {
      await axiosInstance.post(`${API}/seo/google/analytics/configure`, 
        { measurement_id: gaMeasurementId },
        { withCredentials: true }
      );
      toast.success('Google Analytics configuré avec succès');
      setGaMeasurementId('');
      loadGoogleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de configuration');
    } finally {
      setGoogleLoading(false);
    }
  };

  const configureGoogleSearchConsole = async () => {
    if (gscVerificationCode.length < 20) {
      toast.error("Code de vérification invalide");
      return;
    }
    
    setGoogleLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/seo/google/search-console/configure`, 
        { verification_code: gscVerificationCode },
        { withCredentials: true }
      );
      toast.success('Google Search Console configuré');
      setGscVerificationCode('');
      loadGoogleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de configuration');
    } finally {
      setGoogleLoading(false);
    }
  };

  const removeGoogleAnalytics = async () => {
    if (!confirm('Supprimer la configuration Google Analytics ?')) return;
    
    try {
      await axiosInstance.delete(`${API}/seo/google/analytics`, { withCredentials: true });
      toast.success('Google Analytics supprimé');
      loadGoogleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const removeGoogleSearchConsole = async () => {
    if (!confirm('Supprimer la configuration Google Search Console ?')) return;
    
    try {
      await axiosInstance.delete(`${API}/seo/google/search-console`, { withCredentials: true });
      toast.success('Google Search Console supprimé');
      loadGoogleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const saveKeywords = async () => {
    try {
      await axiosInstance.post(`${API}/seo/keywords`, { keywords }, { withCredentials: true });
      toast.success('Mots-clés sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const addKeyword = () => {
    if (newKeyword && !keywords.includes(newKeyword)) {
      setKeywords([...keywords, newKeyword]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (kw) => {
    setKeywords(keywords.filter(k => k !== kw));
  };

  // Analyze a page
  const analyzePage = async (url) => {
    setLoading(true);
    setAnalysisResult(null);
    setAiRecommendations(null);
    setUrlToAnalyze(url);
    
    try {
      const response = await axiosInstance.post(`${API}/seo/ai-recommendations`, {
        url: url,
        keywords: keywords.slice(0, 5)
      }, { withCredentials: true });
      
      setAnalysisResult(response.data.analysis);
      setAiRecommendations(response.data.recommendations);
      loadRecentAnalyses();
      toast.success('Analyse terminée avec recommandations IA');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  // Analyze keyword difficulty
  const analyzeKeywordDifficulty = async () => {
    if (!keywordToAnalyze) {
      toast.error('Entrez un mot-clé');
      return;
    }
    
    setLoading(true);
    setKeywordResult(null);
    
    try {
      const response = await axiosInstance.post(`${API}/seo/keyword-difficulty`, {
        keyword: keywordToAnalyze
      }, { withCredentials: true });
      
      setKeywordResult(response.data);
      loadKeywordHistory();
      toast.success('Analyse de difficulté terminée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const generateLandingPage = async () => {
    if (!selectedCity) {
      toast.error('Sélectionnez une ville');
      return;
    }
    
    setLoading(true);
    setGeneratedContent(null);
    try {
      const response = await axiosInstance.post(`${API}/seo/generate/landing`, {
        city: selectedCity,
        keywords: keywords.slice(0, 3)
      }, { withCredentials: true });
      
      setGeneratedContent(response.data);
      loadGeneratedPages();
      toast.success('Page landing générée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const generateGuideArticle = async () => {
    if (!guideTopic) {
      toast.error('Sujet requis');
      return;
    }
    
    setLoading(true);
    setGeneratedContent(null);
    try {
      const response = await axiosInstance.post(`${API}/seo/generate/guide`, {
        topic: guideTopic,
        keywords: keywords.slice(0, 5)
      }, { withCredentials: true });
      
      setGeneratedContent(response.data);
      loadGeneratedPages();
      toast.success('Article guide généré');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const optimizeMetaTags = async () => {
    if (!currentTitle && !currentDescription) {
      toast.error('Renseignez au moins un champ');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/seo/optimize-meta`, {
        current_title: currentTitle,
        current_description: currentDescription,
        keywords: keywords.slice(0, 3)
      }, { withCredentials: true });
      
      setOptimizedMeta(response.data);
      toast.success('Meta tags optimisés');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'optimisation');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    
    try {
      await axiosInstance.delete(`${API}/seo/pages/${pageId}`, { withCredentials: true });
      toast.success('Page supprimée');
      loadGeneratedPages();
      loadSeoStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const exportPage = async (pageId, format) => {
    try {
      const response = await axiosInstance.get(`${API}/seo/pages/${pageId}/export?format=${format}`, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pageId}.${format === 'markdown' ? 'md' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exporté en ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const schedulePage = async (pageId) => {
    if (!scheduleDate || !scheduleTime) {
      toast.error('Sélectionnez une date et une heure');
      return;
    }
    
    const scheduledAt = `${scheduleDate}T${scheduleTime}:00Z`;
    
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/seo/pages/${pageId}/schedule?scheduled_at=${encodeURIComponent(scheduledAt)}`, {}, { withCredentials: true });
      toast.success('Publication programmée');
      setSchedulingPageId(null);
      setScheduleDate('');
      setScheduleTime('');
      loadGeneratedPages();
      loadSeoStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la programmation');
    } finally {
      setLoading(false);
    }
  };

  const unschedulePage = async (pageId) => {
    try {
      await axiosInstance.post(`${API}/seo/pages/${pageId}/unschedule`, {}, { withCredentials: true });
      toast.success('Programmation annulée');
      loadGeneratedPages();
      loadSeoStats();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const publishNow = async (pageId) => {
    if (!window.confirm('Publier cette page maintenant ?')) return;
    
    try {
      await axiosInstance.post(`${API}/seo/pages/${pageId}/publish-now`, {}, { withCredentials: true });
      toast.success('Page publiée');
      loadGeneratedPages();
      loadSeoStats();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const getStatusBadge = (status, scheduledAt) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">Brouillon</span>;
      case 'scheduled':
        return (
          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {scheduledAt ? new Date(scheduledAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Programmé'}
          </span>
        );
      case 'published':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Publié</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap bg-slate-50 p-2 rounded-lg">
        <Button
          onClick={() => setActiveSection('site-pages')}
          variant={activeSection === 'site-pages' ? 'default' : 'ghost'}
          size="sm"
          className={activeSection === 'site-pages' ? 'bg-blue-600' : ''}
        >
          <Globe className="h-4 w-4 mr-2" />
          Pages du site
        </Button>
        <Button
          onClick={() => setActiveSection('keyword-difficulty')}
          variant={activeSection === 'keyword-difficulty' ? 'default' : 'ghost'}
          size="sm"
          className={activeSection === 'keyword-difficulty' ? 'bg-purple-600' : ''}
        >
          <Target className="h-4 w-4 mr-2" />
          Difficulté mots-clés
        </Button>
        <Button
          onClick={() => setActiveSection('generator')}
          variant={activeSection === 'generator' ? 'default' : 'ghost'}
          size="sm"
          className={activeSection === 'generator' ? 'bg-green-600' : ''}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Générateur IA
        </Button>
        <Button
          onClick={() => setActiveSection('robot-speed')}
          variant={activeSection === 'robot-speed' ? 'default' : 'ghost'}
          size="sm"
          className={activeSection === 'robot-speed' ? 'bg-purple-600' : ''}
        >
          <Rocket className="h-4 w-4 mr-2" />
          Robot Speed
        </Button>
        <Button
          onClick={() => setActiveSection('pages')}
          variant={activeSection === 'pages' ? 'default' : 'ghost'}
          size="sm"
          className={activeSection === 'pages' ? 'bg-amber-600' : ''}
        >
          <FileText className="h-4 w-4 mr-2" />
          Pages ({generatedPages.length})
        </Button>
        <Button
          onClick={() => setActiveSection('keywords')}
          variant={activeSection === 'keywords' ? 'default' : 'ghost'}
          size="sm"
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Mots-clés
        </Button>
        <Button
          onClick={() => setActiveSection('google')}
          variant={activeSection === 'google' ? 'default' : 'ghost'}
          size="sm"
          className={activeSection === 'google' ? 'bg-blue-600' : ''}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Google
        </Button>
      </div>

      {/* ========== SITE PAGES SECTION ========== */}
      {activeSection === 'site-pages' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Pages du site à analyser</h3>
                <p className="text-sm text-slate-500">Cliquez sur "Analyser" pour obtenir le score SEO et les recommandations IA</p>
              </div>
              <Button size="sm" variant="outline" onClick={loadSitePages}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
            </div>

            <div className="space-y-2">
              {sitePages.map((page, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${page.generated ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                      {page.generated ? <FileText className="h-5 w-5" /> : <Globe className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">{page.name}</div>
                      <div className="text-sm text-slate-500">{page.path}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(page.full_url, '_blank')}
                      title="Voir la page"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => analyzePage(page.full_url)}
                      disabled={loading && urlToAnalyze === page.full_url}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {loading && urlToAnalyze === page.full_url ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-1" />
                          Analyser
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Custom URL Analysis */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h4 className="font-medium text-slate-700 mb-3">Analyser une autre URL</h4>
              <div className="flex gap-2">
                <Input
                  value={urlToAnalyze}
                  onChange={(e) => setUrlToAnalyze(e.target.value)}
                  placeholder="https://example.com/page..."
                  className="flex-1"
                />
                <Button onClick={() => analyzePage(urlToAnalyze)} disabled={loading || !urlToAnalyze}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Analyser
                </Button>
              </div>
            </div>
          </div>

          {/* Analysis Results */}
          {analysisResult && !analysisResult.error && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Résultats de l'analyse</h3>
                <Button size="sm" variant="outline" onClick={() => window.open(urlToAnalyze, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Voir la page
                </Button>
              </div>

              {/* Global Score */}
              <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg mb-6">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${getGradeColor(analysisResult.grade)} text-white text-2xl font-bold`}>
                  {analysisResult.grade}
                </div>
                <div>
                  <div className="text-4xl font-bold text-slate-800">{analysisResult.global_score}/100</div>
                  <div className="text-slate-500">Score SEO global</div>
                </div>
              </div>

              {/* Scores Grid */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-6">
                {Object.entries(analysisResult.scores || {}).map(([key, score]) => (
                  <div key={key} className={`p-3 rounded-lg border ${getScoreColor(score)}`}>
                    <div className="text-xs font-medium capitalize mb-1">{key.replace('_', ' ')}</div>
                    <div className="text-xl font-bold">{score}%</div>
                  </div>
                ))}
              </div>

              {/* AI Recommendations */}
              {aiRecommendations && !aiRecommendations.error && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-500" />
                    Recommandations IA
                  </h4>

                  {/* Quick Wins */}
                  {aiRecommendations.quick_wins?.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        Quick Wins (à faire immédiatement)
                      </h5>
                      <ul className="space-y-1">
                        {aiRecommendations.quick_wins.map((win, i) => (
                          <li key={i} className="text-green-700 text-sm flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            {win}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Priority Actions */}
                  {aiRecommendations.priority_actions?.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-medium text-slate-700">Actions prioritaires</h5>
                      {aiRecommendations.priority_actions.map((action, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-slate-800">{action.action}</div>
                              <div className="text-sm text-slate-600 mt-1">{action.details}</div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                action.impact === 'Fort' ? 'bg-red-100 text-red-700' :
                                action.impact === 'Moyen' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                Impact: {action.impact}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                action.effort === 'Facile' ? 'bg-green-100 text-green-700' :
                                action.effort === 'Moyen' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                Effort: {action.effort}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Content Suggestions */}
                  {aiRecommendations.content_suggestions && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-medium text-blue-800 mb-3">Suggestions de contenu optimisé</h5>
                      {aiRecommendations.content_suggestions.title_suggestion && (
                        <div className="mb-3">
                          <div className="text-xs text-blue-600 mb-1">Title suggéré:</div>
                          <div className="flex items-center gap-2">
                            <code className="bg-white px-3 py-2 rounded text-sm flex-1">{aiRecommendations.content_suggestions.title_suggestion}</code>
                            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(aiRecommendations.content_suggestions.title_suggestion)}>
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {aiRecommendations.content_suggestions.meta_description_suggestion && (
                        <div>
                          <div className="text-xs text-blue-600 mb-1">Meta description suggérée:</div>
                          <div className="flex items-center gap-2">
                            <code className="bg-white px-3 py-2 rounded text-sm flex-1">{aiRecommendations.content_suggestions.meta_description_suggestion}</code>
                            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(aiRecommendations.content_suggestions.meta_description_suggestion)}>
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {aiRecommendations.estimated_improvement && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-purple-800">
                        <Award className="h-5 w-5" />
                        <span className="font-medium">Gain estimé après optimisation:</span>
                        <span className="text-purple-900 font-bold">{aiRecommendations.estimated_improvement}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== KEYWORD DIFFICULTY SECTION ========== */}
      {activeSection === 'keyword-difficulty' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Analyse de difficulté des mots-clés</h3>
            <p className="text-sm text-slate-500 mb-4">Évaluez la compétitivité d'un mot-clé et obtenez des suggestions alternatives</p>
            
            <div className="flex gap-2 mb-6">
              <Input
                value={keywordToAnalyze}
                onChange={(e) => setKeywordToAnalyze(e.target.value)}
                placeholder="Ex: investissement locatif Lyon"
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && analyzeKeywordDifficulty()}
              />
              <Button onClick={analyzeKeywordDifficulty} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4 mr-2" />}
                Analyser
              </Button>
            </div>

            {/* Keyword Result */}
            {keywordResult && !keywordResult.error && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-slate-800">{keywordResult.difficulty_score}</div>
                    <div className="text-sm text-slate-500">Score de difficulté</div>
                    <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(keywordResult.difficulty_level)}`}>
                      {keywordResult.difficulty_level}
                    </span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{keywordResult.opportunity_score}</div>
                    <div className="text-sm text-slate-500">Score d'opportunité</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-slate-800">{keywordResult.search_volume_estimate}</div>
                    <div className="text-sm text-slate-500">Volume de recherche</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-slate-800">{keywordResult.cpc_estimate}</div>
                    <div className="text-sm text-slate-500">CPC estimé</div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Related Keywords */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 mb-2">Mots-clés connexes moins compétitifs</h5>
                    <div className="flex flex-wrap gap-2">
                      {keywordResult.related_keywords?.map((kw, i) => (
                        <span key={i} className="px-3 py-1 bg-white rounded-full text-sm text-blue-700 border border-blue-200">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Long Tail Suggestions */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h5 className="font-medium text-green-800 mb-2">Variations longue traîne</h5>
                    <div className="flex flex-wrap gap-2">
                      {keywordResult.long_tail_suggestions?.map((kw, i) => (
                        <span key={i} className="px-3 py-1 bg-white rounded-full text-sm text-green-700 border border-green-200">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Strategy */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h5 className="font-medium text-purple-800 mb-2">Stratégie recommandée</h5>
                  <p className="text-purple-700">{keywordResult.content_strategy}</p>
                  <div className="mt-2 text-sm text-purple-600">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Temps estimé pour se positionner: <strong>{keywordResult.estimated_time_to_rank}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Keyword History */}
          {keywordHistory.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Historique des analyses</h3>
              <div className="space-y-2">
                {keywordHistory.map((analysis, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-medium text-slate-800">{analysis.keyword}</div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDifficultyColor(analysis.result?.difficulty_level)}`}>
                        {analysis.result?.difficulty_level}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>Score: {analysis.result?.difficulty_score}/100</span>
                      <span>{new Date(analysis.analyzed_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== KEYWORDS SECTION ========== */}
      {activeSection === 'keywords' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Mots-clés cibles</h3>
          <p className="text-sm text-slate-500 mb-4">Ces mots-clés seront utilisés pour l'analyse et la génération de contenu.</p>
          
          <div className="flex gap-2 mb-4">
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder="Ajouter un mot-clé..."
              className="flex-1"
              onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
            />
            <Button onClick={addKeyword}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {keywords.map((kw, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                {kw}
                <button onClick={() => removeKeyword(kw)} className="hover:text-red-600">
                  <XCircle className="h-4 w-4" />
                </button>
              </span>
            ))}
          </div>
          
          <Button onClick={saveKeywords} className="bg-green-600 hover:bg-green-700">
            Sauvegarder les mots-clés
          </Button>
        </div>
      )}

      {/* ========== GENERATOR SECTION ========== */}
      {activeSection === 'generator' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Générateur de contenu SEO</h3>
            
            <div className="flex gap-2 mb-6">
              <Button
                onClick={() => setGeneratorType('landing')}
                variant={generatorType === 'landing' ? 'default' : 'outline'}
                size="sm"
              >
                <Globe className="h-4 w-4 mr-2" />
                Page Landing Locale
              </Button>
              <Button
                onClick={() => setGeneratorType('guide')}
                variant={generatorType === 'guide' ? 'default' : 'outline'}
                size="sm"
              >
                <FileText className="h-4 w-4 mr-2" />
                Article Guide
              </Button>
            </div>

            {generatorType === 'landing' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ville cible</label>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-lg"
                  >
                    <option value="">Sélectionnez une ville...</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={generateLandingPage} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Générer la page landing
                </Button>
              </div>
            )}

            {generatorType === 'guide' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sujet de l'article</label>
                  <Input
                    value={guideTopic}
                    onChange={(e) => setGuideTopic(e.target.value)}
                    placeholder="Ex: Comment investir en LMNP en 2025"
                  />
                </div>
                <Button onClick={generateGuideArticle} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Générer l'article guide
                </Button>
              </div>
            )}
          </div>

          {/* Generated Content Preview */}
          {generatedContent && !generatedContent.error && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Contenu généré</h3>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(JSON.stringify(generatedContent, null, 2))}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copier JSON
                </Button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm font-medium text-blue-800 mb-1">Meta Title ({generatedContent.meta_title?.length} chars)</div>
                  <p className="text-blue-900 font-medium">{generatedContent.meta_title}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm font-medium text-green-800 mb-1">Meta Description ({generatedContent.meta_description?.length} chars)</div>
                  <p className="text-green-900">{generatedContent.meta_description}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-sm font-medium text-purple-800 mb-1">H1</div>
                  <p className="text-purple-900 text-xl font-bold">{generatedContent.h1}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== PAGES SECTION ========== */}
      {activeSection === 'pages' && (
        <div className="space-y-6">
          {seoStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{seoStats.total_pages || 0}</div>
                    <div className="text-xs text-slate-500">Pages totales</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{seoStats.pages_by_status?.published || 0}</div>
                    <div className="text-xs text-slate-500">Publiées</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{seoStats.pages_by_status?.scheduled || 0}</div>
                    <div className="text-xs text-slate-500">Programmées</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{seoStats.total_analyses || 0}</div>
                    <div className="text-xs text-slate-500">Analyses SEO</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Pages générées</h3>
              <Button size="sm" variant="outline" onClick={() => { loadGeneratedPages(); loadSeoStats(); }}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
            </div>

            {generatedPages.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune page générée</p>
                <Button className="mt-4" onClick={() => setActiveSection('generator')}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Générer du contenu
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {generatedPages.map((page, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          page.type === 'landing_page' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                        }`}>
                          {page.type === 'landing_page' ? <Globe className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            {page.type === 'landing_page' ? `Landing: ${page.city}` : `Guide: ${page.topic?.substring(0, 40)}...`}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-2">
                            {new Date(page.created_at).toLocaleDateString('fr-FR')}
                            {getStatusBadge(page.status, page.scheduled_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {page.status === 'draft' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setSchedulingPageId(schedulingPageId === page.page_id ? null : page.page_id)}>
                              <Calendar className="h-4 w-4 mr-1" />
                              Programmer
                            </Button>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => publishNow(page.page_id)}>
                              <Send className="h-4 w-4 mr-1" />
                              Publier
                            </Button>
                          </>
                        )}
                        {page.status === 'scheduled' && (
                          <Button size="sm" variant="outline" className="text-amber-600" onClick={() => unschedulePage(page.page_id)}>
                            <X className="h-4 w-4 mr-1" />
                            Annuler
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => exportPage(page.page_id, 'html')} title="Export HTML">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(JSON.stringify(page.content, null, 2))} title="Copier JSON">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deletePage(page.page_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {schedulingPageId === page.page_id && (
                      <div className="mt-4 p-4 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <Calendar className="h-5 w-5 text-amber-600" />
                          <span className="font-medium text-slate-800">Programmer la publication</span>
                        </div>
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <label className="block text-sm text-slate-600 mb-1">Date</label>
                            <Input
                              type="date"
                              value={scheduleDate}
                              onChange={(e) => setScheduleDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-sm text-slate-600 mb-1">Heure</label>
                            <Input
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                            />
                          </div>
                          <Button onClick={() => schedulePage(page.page_id)} disabled={loading} className="bg-amber-600 hover:bg-amber-700">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4 mr-1" />}
                            Confirmer
                          </Button>
                          <Button variant="ghost" onClick={() => setSchedulingPageId(null)}>
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== ROBOT SPEED SECTION ========== */}
      {activeSection === 'robot-speed' && (
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Rocket className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Robot Speed SEO</h3>
                <p className="text-sm text-slate-600">Intégration CMS headless pour articles IA</p>
              </div>
              <a 
                href="https://www.robot-speed.com/fr/dashboard" 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-auto text-purple-600 hover:text-purple-700 flex items-center gap-1 text-sm"
              >
                Dashboard Robot Speed
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
            
            {robotSpeedStatus?.configured ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium text-green-700">Configuré</span>
                </div>
                <div className="text-sm text-slate-600">
                  Clé: <code className="bg-slate-100 px-2 py-1 rounded">{robotSpeedStatus.api_key_preview}</code>
                </div>
                <div className="text-sm text-slate-600">
                  {robotSpeedStatus.synced_articles} article(s) synchronisé(s)
                </div>
                {robotSpeedStatus.last_sync && (
                  <div className="text-xs text-slate-500">
                    Dernière sync: {new Date(robotSpeedStatus.last_sync).toLocaleString('fr-FR')}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <span className="text-sm text-amber-700">Non configuré</span>
              </div>
            )}
          </div>

          {/* Configuration */}
          {!robotSpeedStatus?.configured && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuration
              </h4>
              <p className="text-sm text-slate-600 mb-4">
                Obtenez votre clé API depuis{' '}
                <a 
                  href="https://www.robot-speed.com/fr/dashboard/settings" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline"
                >
                  Robot Speed Settings
                </a>
              </p>
              <div className="flex gap-3">
                <Input
                  placeholder="rs_votre_cle_api_ici"
                  value={robotSpeedApiKey}
                  onChange={(e) => setRobotSpeedApiKey(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={configureRobotSpeed}
                  disabled={robotSpeedLoading || !robotSpeedApiKey}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {robotSpeedLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Configurer'}
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          {robotSpeedStatus?.configured && (
            <div className="flex gap-3">
              <Button 
                onClick={syncRobotSpeed}
                disabled={robotSpeedLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {robotSpeedLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Synchroniser les articles
              </Button>
              <Button 
                variant="outline"
                onClick={loadRobotSpeedArticles}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Rafraîchir
              </Button>
            </div>
          )}

          {/* Articles List */}
          {robotSpeedStatus?.configured && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Articles Robot Speed ({robotSpeedArticles.length})
              </h4>
              
              {robotSpeedArticles.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Rocket className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucun article synchronisé</p>
                  <p className="text-sm mt-1">Cliquez sur "Synchroniser" pour importer vos articles</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {robotSpeedArticles.map((article) => (
                    <div 
                      key={article.robot_speed_id} 
                      className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="font-medium text-slate-900 mb-1">{article.title}</h5>
                          <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span>{article.word_count} mots</span>
                            <span>•</span>
                            <span>{article.reading_time} min de lecture</span>
                            {article.target_keyword && (
                              <>
                                <span>•</span>
                                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                  {article.target_keyword}
                                </span>
                              </>
                            )}
                            {article.published_locally && (
                              <>
                                <span>•</span>
                                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Publié
                                </span>
                              </>
                            )}
                          </div>
                          {article.seo_keywords && article.seo_keywords.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {article.seo_keywords.slice(0, 5).map((kw, i) => (
                                <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          {article.featured_image_url && (
                            <img 
                              src={article.featured_image_url} 
                              alt={article.featured_image_alt || article.title}
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100">
                        <div className="text-xs text-slate-500">
                          Publié le {new Date(article.published_at).toLocaleDateString('fr-FR')}
                        </div>
                        <div className="flex gap-2">
                          {!article.published_locally && (
                            <Button 
                              size="sm" 
                              onClick={() => publishRobotSpeedArticle(article.robot_speed_id)}
                              disabled={robotSpeedLoading}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {robotSpeedLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="h-4 w-4 mr-1" />
                                  Publier sur le site
                                </>
                              )}
                            </Button>
                          )}
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== GOOGLE TOOLS SECTION ========== */}
      {activeSection === 'google' && (
        <div className="space-y-6">
          {/* Google Analytics */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Google Analytics 4</h3>
                <p className="text-sm text-slate-600">Suivi du trafic et des conversions</p>
              </div>
              {googleStatus?.google_analytics?.configured && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={removeGoogleAnalytics}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {googleStatus?.google_analytics?.configured ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-green-700">Configuré et actif</span>
                </div>
                <div className="text-sm text-slate-600">
                  <span className="font-medium">ID de mesure:</span>{' '}
                  <code className="bg-slate-100 px-2 py-1 rounded">{googleStatus.google_analytics.measurement_id}</code>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Le script Google Analytics est automatiquement chargé sur toutes les pages.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Entrez votre ID de mesure Google Analytics 4 (format: G-XXXXXXXXXX).
                  Vous le trouverez dans{' '}
                  <a 
                    href="https://analytics.google.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Google Analytics → Admin → Flux de données
                  </a>
                </p>
                <div className="flex gap-3">
                  <Input
                    placeholder="G-XXXXXXXXXX"
                    value={gaMeasurementId}
                    onChange={(e) => setGaMeasurementId(e.target.value.toUpperCase())}
                    className="flex-1"
                  />
                  <Button 
                    onClick={configureGoogleAnalytics}
                    disabled={googleLoading || !gaMeasurementId}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Configurer'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Google Search Console */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Google Search Console</h3>
                <p className="text-sm text-slate-600">Suivi du référencement et des performances de recherche</p>
              </div>
              {googleStatus?.google_search_console?.configured && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={removeGoogleSearchConsole}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {googleStatus?.google_search_console?.configured ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="font-medium text-green-700">Configuré</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">
                    Ajoutez cette balise meta dans le <code>&lt;head&gt;</code> de votre site:
                  </p>
                  <div className="bg-slate-800 text-green-400 p-3 rounded-lg text-sm font-mono overflow-x-auto">
                    {`<meta name="google-site-verification" content="${googleStatus.google_search_console.verification_code}" />`}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => {
                      navigator.clipboard.writeText(`<meta name="google-site-verification" content="${googleStatus.google_search_console.verification_code}" />`);
                      toast.success('Balise meta copiée');
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copier la balise
                  </Button>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Étapes de vérification:</h4>
                  <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
                    <li>Ajoutez la balise meta dans le fichier <code>index.html</code> de votre site</li>
                    <li>Déployez votre site</li>
                    <li>Retournez sur <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Search Console</a></li>
                    <li>Cliquez sur "Vérifier"</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Entrez le code de vérification de Google Search Console.
                  Vous le trouverez dans{' '}
                  <a 
                    href="https://search.google.com/search-console" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Search Console → Paramètres → Vérification de la propriété → Balise HTML
                  </a>
                </p>
                <div className="flex gap-3">
                  <Input
                    placeholder="Code de vérification (contenu de la balise meta)"
                    value={gscVerificationCode}
                    onChange={(e) => setGscVerificationCode(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={configureGoogleSearchConsole}
                    disabled={googleLoading || !gscVerificationCode}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Configurer'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-6">
            <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Guide rapide
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Google Analytics</h5>
                <ul className="list-disc list-inside space-y-1">
                  <li>Suivez le nombre de visiteurs</li>
                  <li>Analysez les sources de trafic</li>
                  <li>Mesurez les conversions (simulations)</li>
                  <li>Identifiez les pages les plus populaires</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Google Search Console</h5>
                <ul className="list-disc list-inside space-y-1">
                  <li>Surveillez votre positionnement Google</li>
                  <li>Découvrez les requêtes qui amènent du trafic</li>
                  <li>Identifiez les erreurs d'indexation</li>
                  <li>Soumettez votre sitemap</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
